import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './AISettings.css';

const API = '/api/ai';

const DEFAULT_SYSTEM_PROMPT = `Ты — AI-консультант компании СЕТЬМОНТАЖ (inet-sm.ru).
Компания работает с 2011 года на Черноморском побережье (Анапа—Сочи).

Услуги: гостиничные решения, сети, видеонаблюдение, телефония, автоматизация и др.
Контакты: 8-800-600-65-41, info@inet-sm.ru

Отвечай дружелюбно на русском, кратко (2-4 предложения).`;

const PROVIDER_TYPES = [
  { value: 'openai', label: 'OpenAI', needsKey: true },
  { value: 'anthropic', label: 'Anthropic', needsKey: true },
  { value: 'openrouter', label: 'OpenRouter', needsKey: true, defaultUrl: 'https://openrouter.ai/api/v1' },
  { value: 'ollama', label: 'Ollama', needsKey: false, defaultUrl: 'http://localhost:11434/v1' },
  { value: 'lmstudio', label: 'LM Studio', needsKey: false, defaultUrl: 'http://localhost:1234/v1' },
  { value: 'groq', label: 'Groq', needsKey: true, defaultUrl: 'https://api.groq.com/openai/v1' },
  { value: 'custom', label: 'Custom', needsKey: false }
];

const emptyProvider = () => ({
  name: '', provider: 'openai', apiKey: '', model: 'gpt-4o-mini', baseUrl: '',
  maxTokens: 500, temperature: 0.7, requestTimeoutMs: 25000, enabled: true
});

const initSettings = {
  enabled: false,
  systemPrompt: '',
  welcomeMessage: 'Здравствуйте! Я AI-консультант СЕТЬМОНТАЖ. Чем могу помочь? 😊',
  collectContacts: true,
  contactTriggerMessage: 'Хотите, чтобы наш менеджер связался с вами? Оставьте свои контакты!',
  rateLimitPerMinute: 10,
  maxMessagesPerSession: 20,
  sessionTimeoutMinutes: 30,
  widgetPosition: 'right',
  widgetColor: '#3b82f6',
  workingHours: { start: '09:00', end: '18:00', timezone: 'Europe/Moscow' },
  savedPrompts: []
};

const statusDot = (status) => {
  if (status === 'online') return { className: 'status-online', title: 'Онлайн' };
  if (status === 'error') return { className: 'status-error', title: 'Ошибка' };
  return { className: 'status-unknown', title: 'Не проверен' };
};

const AISettings = ({ token, showMsg }) => {
  const [settings, setSettings] = useState(initSettings);
  const [providers, setProviders] = useState([]);
  const [activeProviderId, setActiveProviderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [promptName, setPromptName] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyProvider());
  const [showAddForm, setShowAddForm] = useState(false);
  const [dragId, setDragId] = useState(null);

  const h = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadAll = useCallback(async () => {
    try {
      const [settingsRes, provRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, { headers: h }),
        axios.get(`${API}/admin/providers`, { headers: h })
      ]);
      if (settingsRes.data && Object.keys(settingsRes.data).length > 0) {
        setSettings(prev => Object.keys(initSettings).reduce((acc, key) => {
          acc[key] = settingsRes.data[key] !== undefined ? settingsRes.data[key] : initSettings[key];
          return acc;
        }, {}));
      }
      setProviders(provRes.data.providers || []);
      setActiveProviderId(provRes.data.activeProviderId || null);
    } catch {
      showMsg('Ошибка загрузки настроек AI');
    } finally {
      setLoading(false);
    }
  }, [h, showMsg]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        ...settings,
        activeProviderId
      }, { headers: h });
      showMsg('✅ Настройки виджета сохранены');
    } catch {
      showMsg('❌ Ошибка сохранения');
    }
  };

  const testFailover = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/test`, {}, { headers: h });
      setTestResult({
        success: true,
        message: res.data.message,
        response: res.data.response,
        provider: res.data.provider
      });
      loadAll();
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.message || 'Ошибка' });
      loadAll();
    } finally {
      setTestLoading(false);
    }
  };

  const healthCheckAll = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/admin/providers/healthcheck`, {}, { headers: h });
      showMsg('✅ Проверка провайдеров завершена');
      loadAll();
    } catch {
      showMsg('❌ Ошибка проверки');
    } finally {
      setTestLoading(false);
    }
  };

  const saveProvider = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/admin/providers/${editingId}`, editForm, { headers: h });
        showMsg('✅ Провайдер обновлён');
      } else {
        await axios.post(`${API}/admin/providers`, editForm, { headers: h });
        showMsg('✅ Провайдер добавлен');
      }
      setEditingId(null);
      setShowAddForm(false);
      setEditForm(emptyProvider());
      loadAll();
    } catch (e) {
      showMsg('❌ ' + (e.response?.data?.message || 'Ошибка сохранения'));
    }
  };

  const startEdit = (p) => {
    setEditingId(p._id);
    setEditForm({
      name: p.name,
      provider: p.provider,
      apiKey: p.apiKey || '',
      model: p.model,
      baseUrl: p.baseUrl || '',
      maxTokens: p.maxTokens,
      temperature: p.temperature,
      requestTimeoutMs: p.requestTimeoutMs ?? 25000,
      enabled: p.enabled
    });
    setShowAddForm(true);
  };

  const deleteProvider = async (id) => {
    if (!window.confirm('Удалить этот провайдер?')) return;
    try {
      await axios.delete(`${API}/admin/providers/${id}`, { headers: h });
      showMsg('🗑️ Удалено');
      loadAll();
    } catch {
      showMsg('❌ Ошибка удаления');
    }
  };

  const toggleProvider = async (id) => {
    try {
      await axios.post(`${API}/admin/providers/${id}/toggle`, {}, { headers: h });
      loadAll();
    } catch {
      showMsg('❌ Ошибка');
    }
  };

  const activateProvider = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/providers/${id}/activate`, {}, { headers: h });
      setActiveProviderId(res.data.activeProviderId);
      showMsg('✅ Провайдер выбран как активный (первый в очереди)');
      loadAll();
    } catch {
      showMsg('❌ Ошибка');
    }
  };

  const testOneProvider = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/providers/${id}/test`, {}, { headers: h });
      showMsg(`✅ ${res.data.provider?.name}: ${res.data.response}`);
      loadAll();
    } catch (e) {
      showMsg(`❌ ${e.response?.data?.message || 'Ошибка'}`);
      loadAll();
    }
  };

  const moveProvider = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= providers.length) return;
    const ordered = [...providers];
    const [item] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, item);
    const orderedIds = ordered.map(p => p._id);
    setProviders(ordered);
    try {
      await axios.post(`${API}/admin/providers/reorder`, { orderedIds }, { headers: h });
    } catch {
      loadAll();
    }
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const savePrompt = () => {
    if (!promptName.trim() || !settings.systemPrompt.trim()) return;
    const prompts = settings.savedPrompts || [];
    if (prompts.find(p => p.name === promptName.trim())) {
      showMsg('⚠️ Промпт с таким именем уже существует');
      return;
    }
    updateField('savedPrompts', [...prompts, { name: promptName.trim(), content: settings.systemPrompt }]);
    setPromptName('');
    showMsg(`✅ Промпт "${promptName.trim()}" сохранён`);
  };

  const loadPrompt = (name) => {
    const prompt = (settings.savedPrompts || []).find(p => p.name === name);
    if (prompt) {
      updateField('systemPrompt', prompt.content);
      setSelectedPrompt(name);
    }
  };

  const deletePrompt = (name) => {
    updateField('savedPrompts', (settings.savedPrompts || []).filter(p => p.name !== name));
    if (selectedPrompt === name) setSelectedPrompt('');
  };

  const provType = PROVIDER_TYPES.find(p => p.value === editForm.provider);

  if (loading) return <div className="ai-settings-loading">⏳ Загрузка...</div>;

  const activeProv = providers.find(p => p._id === activeProviderId || p._id?.toString() === activeProviderId?.toString());

  return (
    <div className="ai-settings">
      <h2>🤖 AI-чат на сайте</h2>

      <div className="ai-settings-section">
        <h3>Основные настройки виджета</h3>
        <div className="ai-form-grid">
          <div className="ai-form-group toggle-group">
            <label className="toggle-label">
              <input type="checkbox" checked={!!settings.enabled} onChange={e => updateField('enabled', e.target.checked)} />
              <span className="toggle-switch"></span> Включить AI-чат
            </label>
          </div>
          <div className="ai-form-group">
            <label>Приветственное сообщение</label>
            <input type="text" value={settings.welcomeMessage || ''} onChange={e => updateField('welcomeMessage', e.target.value)} />
          </div>
          <div className="ai-form-group">
            <label>Позиция виджета</label>
            <select value={settings.widgetPosition} onChange={e => updateField('widgetPosition', e.target.value)}>
              <option value="right">Справа</option>
              <option value="left">Слева</option>
            </select>
          </div>
          <div className="ai-form-group">
            <label>Цвет виджета</label>
            <div className="color-picker-row">
              <input type="color" value={settings.widgetColor || '#3b82f6'} onChange={e => updateField('widgetColor', e.target.value)} />
              <span className="color-value">{settings.widgetColor}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ai-settings-section ai-providers-section">
        <div className="ai-providers-header">
          <h3>Провайдеры AI (приоритет и failover)</h3>
          <div className="ai-providers-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={healthCheckAll} disabled={testLoading}>🔍 Проверить все</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={testFailover} disabled={testLoading}>🧪 Тест (с failover)</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { setShowAddForm(true); setEditingId(null); setEditForm(emptyProvider()); }}>➕ Добавить</button>
          </div>
        </div>

        {activeProv && (
          <div className="ai-active-banner">
            <span className="status-dot status-online"></span>
            Активный провайдер: <strong>{activeProv.name}</strong> ({activeProv.provider} / {activeProv.model})
          </div>
        )}

        {testResult && (
          <div className={`ai-test-result ${testResult.success ? 'success' : 'error'}`}>
            {testResult.success ? `✅ ${testResult.message}` : `❌ ${testResult.message}`}
            {testResult.provider && <p className="ai-test-response">Использован: {testResult.provider.name} ({testResult.provider.model})</p>}
            {testResult.response && <p className="ai-test-response">Ответ: {testResult.response}</p>}
          </div>
        )}

        {providers.length === 0 ? (
          <p className="ai-providers-empty">Нет провайдеров. Добавьте хотя бы один или сохраните legacy-настройки при первом открытии.</p>
        ) : (
          <ul className="ai-provider-list">
            {providers.map((p, idx) => {
              const dot = statusDot(p.status);
              const isActive = p._id === activeProviderId || p._id?.toString() === activeProviderId?.toString();
              return (
                <li
                  key={p._id}
                  className={`ai-provider-card ${!p.enabled ? 'disabled' : ''} ${isActive ? 'active' : ''}`}
                  draggable
                  onDragStart={() => setDragId(p._id)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => {
                    if (!dragId || dragId === p._id) return;
                    const fromIdx = providers.findIndex(x => x._id === dragId);
                    moveProvider(fromIdx, idx);
                    setDragId(null);
                  }}
                >
                  <div className="ai-provider-card-top">
                    <span className={`status-dot ${dot.className}`} title={dot.title}></span>
                    <strong>{p.name}</strong>
                    {isActive && <span className="ai-badge-active">АКТИВНЫЙ</span>}
                    <span className="ai-provider-priority">Приоритет: {p.priority}</span>
                    <label className="toggle-label compact">
                      <input type="checkbox" checked={!!p.enabled} onChange={() => toggleProvider(p._id)} />
                      <span className="toggle-switch small"></span>
                    </label>
                  </div>
                  <div className="ai-provider-meta">
                    {p.provider} · {p.model}
                    {p.maxTokens && ` · ${p.maxTokens} tok`}
                  </div>
                  {p.status === 'error' && p.lastError && (
                    <div className="ai-provider-error">❌ {p.lastError}</div>
                  )}
                  {p.lastCheckedAt && (
                    <div className="ai-provider-checked">Проверен: {new Date(p.lastCheckedAt).toLocaleString('ru-RU')}</div>
                  )}
                  <div className="ai-provider-card-actions">
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => moveProvider(idx, idx - 1)} disabled={idx === 0}>↑</button>
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => moveProvider(idx, idx + 1)} disabled={idx === providers.length - 1}>↓</button>
                    {!isActive && (
                      <button type="button" className="btn btn-sm btn-outline" onClick={() => activateProvider(p._id)}>Сделать активным</button>
                    )}
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => testOneProvider(p._id)}>Тест</button>
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => startEdit(p)}>✏️</button>
                    <button type="button" className="btn btn-sm btn-outline danger" onClick={() => deleteProvider(p._id)}>🗑️</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {showAddForm && (
          <div className="ai-provider-form">
            <h4>{editingId ? 'Редактировать провайдер' : 'Новый провайдер'}</h4>
            <div className="ai-form-grid">
              <div className="ai-form-group">
                <label>Название (для себя)</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="OpenAI GPT-4o mini" />
              </div>
              <div className="ai-form-group">
                <label>Тип</label>
                <select value={editForm.provider} onChange={e => {
                  const t = PROVIDER_TYPES.find(x => x.value === e.target.value);
                  setEditForm({
                    ...editForm,
                    provider: e.target.value,
                    baseUrl: t?.defaultUrl || editForm.baseUrl
                  });
                }}>
                  {PROVIDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="ai-form-group">
                <label>Модель</label>
                <input type="text" value={editForm.model} onChange={e => setEditForm({ ...editForm, model: e.target.value })} />
              </div>
              {(provType?.needsKey || editForm.provider === 'openrouter') && (
                <div className="ai-form-group">
                  <label>API Key</label>
                  <input type="password" value={editForm.apiKey} onChange={e => setEditForm({ ...editForm, apiKey: e.target.value })} />
                </div>
              )}
              <div className="ai-form-group">
                <label>Base URL</label>
                <input type="text" value={editForm.baseUrl} onChange={e => setEditForm({ ...editForm, baseUrl: e.target.value })} placeholder={provType?.defaultUrl || ''} />
              </div>
              <div className="ai-form-group">
                <label>Max Tokens</label>
                <input type="number" value={editForm.maxTokens} onChange={e => setEditForm({ ...editForm, maxTokens: parseInt(e.target.value) || 500 })} />
              </div>
              <div className="ai-form-group">
                <label>Temperature</label>
                <input type="number" step="0.1" min="0" max="2" value={editForm.temperature} onChange={e => setEditForm({ ...editForm, temperature: parseFloat(e.target.value) || 0.7 })} />
              </div>
              <div className="ai-form-group">
                <label>Таймаут запроса (мс)</label>
                <input type="number" min="5000" max="300000" step="1000" value={editForm.requestTimeoutMs ?? 25000} onChange={e => setEditForm({ ...editForm, requestTimeoutMs: parseInt(e.target.value, 10) || 25000 })} />
              </div>
            </div>
            <div className="ai-provider-form-actions">
              <button type="button" className="btn btn-primary btn-sm" onClick={saveProvider} disabled={!editForm.name.trim()}>💾 Сохранить</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowAddForm(false); setEditingId(null); }}>Отмена</button>
            </div>
          </div>
        )}
      </div>

      <div className="ai-settings-section">
        <h3>System Prompt (общий для всех провайдеров)</h3>
        <div className="ai-prompt-controls">
          <button type="button" className="btn btn-sm btn-outline" onClick={() => { updateField('systemPrompt', DEFAULT_SYSTEM_PROMPT); setSelectedPrompt(''); }}>🔄 По умолчанию</button>
          <div className="ai-prompt-save-row">
            <input type="text" placeholder="Имя промпта" value={promptName} onChange={e => setPromptName(e.target.value)} />
            <button type="button" className="btn btn-sm btn-primary" onClick={savePrompt} disabled={!promptName.trim()}>💾 Сохранить</button>
          </div>
          {(settings.savedPrompts || []).length > 0 && (
            <div className="ai-saved-prompts">
              <label>Сохранённые:</label>
              <div className="ai-prompt-tags">
                {settings.savedPrompts.map(p => (
                  <span key={p.name} className={`ai-prompt-tag ${selectedPrompt === p.name ? 'active' : ''}`}>
                    <span onClick={() => loadPrompt(p.name)}>{p.name}</span>
                    <button type="button" className="ai-prompt-tag-del" onClick={() => deletePrompt(p.name)}>✕</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <textarea
          value={settings.systemPrompt ?? ''}
          onChange={e => updateField('systemPrompt', e.target.value)}
          rows={8}
          placeholder="Инструкция для AI..."
        />
      </div>

      <div className="ai-settings-section">
        <h3>Лимиты и контакты</h3>
        <div className="ai-form-grid">
          <div className="ai-form-group">
            <label>Лимит сообщений/мин</label>
            <input type="number" min="1" max="60" value={settings.rateLimitPerMinute} onChange={e => updateField('rateLimitPerMinute', parseInt(e.target.value) || 10)} />
          </div>
          <div className="ai-form-group">
            <label>Макс. сообщений за сессию</label>
            <input type="number" min="5" max="100" value={settings.maxMessagesPerSession} onChange={e => updateField('maxMessagesPerSession', parseInt(e.target.value) || 20)} />
          </div>
          <div className="ai-form-group">
            <label>Таймаут сессии (мин)</label>
            <input type="number" min="5" max="120" value={settings.sessionTimeoutMinutes} onChange={e => updateField('sessionTimeoutMinutes', parseInt(e.target.value) || 30)} />
          </div>
          <div className="ai-form-group toggle-group">
            <label className="toggle-label">
              <input type="checkbox" checked={!!settings.collectContacts} onChange={e => updateField('collectContacts', e.target.checked)} />
              <span className="toggle-switch"></span> Сбор контактов
            </label>
          </div>
          <div className="ai-form-group full-width">
            <label>Текст запроса контактов</label>
            <input type="text" value={settings.contactTriggerMessage || ''} onChange={e => updateField('contactTriggerMessage', e.target.value)} />
          </div>
        </div>
      </div>

      <button type="button" className="btn btn-primary btn-large" onClick={saveSettings}>💾 Сохранить настройки виджета</button>
    </div>
  );
};

export default AISettings;
